import type {
  FilterParams,
  SearchParams,
} from '@/modules/common/context/QueryParams/models';
import type {Recommendation} from '@/modules/common/hooks/useRecommendations/models';

import Cookies from 'js-cookie';
import {useEffect, useState} from 'react';

import {getRecommendations} from '@/api/bffHotel';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {extractGetRecommendationsParams} from '@/modules/common/hooks/useRecommendations/extractGetRecommendationsParams';
import {mapResponseToRecommendation} from '@/modules/common/hooks/useRecommendations/mapResponseToRecommendation';
import {getPlacementId} from '@/modules/common/hooks/useRecommendations/utils/getPlacementId';
import {
  EVENT_BD4_RECOMMENDED_LOADED,
  useTracking,
} from '@/modules/common/hooks/useTracking';

export const useRecommendations = ({
  enabled = true,
  searchParams,
  filterParams,
}: {
  enabled?: boolean;
  searchParams: SearchParams;
  filterParams: FilterParams;
}) => {
  const [recommendations, setRecommendations] = useState<{
    recommendations?: Array<Recommendation>;
    isLoading: boolean;
  }>({isLoading: true});
  const {trackEvent, dispatchGenericTrackingEvent} = useTracking();

  const currentPage = useCurrentPage();
  const currentTravelType = searchParams.travelType;

  const placementId = getPlacementId({
    page: currentPage,
    travelType: currentTravelType,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setRecommendations({isLoading: true});

    const userId = Cookies.get('bd4ti') || 'unknown';
    const requestParameters = extractGetRecommendationsParams(
      userId,
      searchParams,
      filterParams
    );

    if (requestParameters === null) {
      return;
    }

    requestParameters.placementId = placementId;
    requestParameters.baseUrl = import.meta.env.VITE_RECOMMENDATION_BASE_URL;

    getRecommendations(requestParameters)
      .then((response) => {
        const pax = searchParams.rooms.travellerCount;
        const nextRecommendations =
          response.status === 200
            ? mapResponseToRecommendation(response.data, pax)
            : [];
        setRecommendations({
          recommendations: nextRecommendations,
          isLoading: false,
        });

        if (nextRecommendations.length === 0) {
          dispatchGenericTrackingEvent({
            event: 'reconoview',
            event_action: 'BD4_recommender_not_loaded',
            listId: placementId,
          });
        } else {
          trackEvent(EVENT_BD4_RECOMMENDED_LOADED, {
            recommendations: nextRecommendations,
          });
        }
      })
      .catch(() => {
        setRecommendations({isLoading: false});
      });
  }, [
    filterParams,
    searchParams,
    enabled,
    placementId,
    trackEvent,
    dispatchGenericTrackingEvent,
  ]);

  return recommendations;
};
